import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { quoteTimeline, settings } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';

/**
 * Cancelar ou negar a cotacao (P-COT-3).
 *
 * Os dois sao a transicao para declinada que ja existe, com a mesma permissao e o
 * motivo obrigatorio: o cancelamento grava a situacao "Cancelada" ("Cancelar cotacao"
 * do OnPoint); a negativa grava a situacao que a empresa configurou para o declinio
 * (SaveProposalDenialJustification). O motivo e o padronizado do catalogo, a
 * justificativa ou os dois. O aviso ao corretor e regra pendente (SUB-016): nada e
 * enviado.
 *
 * O catalogo de motivos pede a leitura da configuracao; sem ela, o dialogo fica so com
 * a justificativa.
 */
export function QuoteDeclineDialog({
  operationId,
  mode,
  open,
  onClose,
}: {
  operationId: string;
  mode: 'cancelamento' | 'negativa';
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const cancelamento = mode === 'cancelamento';

  const motivos = useQuery({
    queryKey: ['settings', 'decline-reasons', 'cotacao', mode],
    queryFn: ({ signal }) =>
      settings.declineReasons(cancelamento ? { scope: 'cancelamento' } : {}, signal),
    enabled: open,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const opcoes = (motivos.data ?? [])
    .filter((item) => item.active)
    .filter((item) => cancelamento || item.scope !== 'cancelamento')
    .map((item) => ({ value: item.id, label: item.label }));

  const enviar = useMutation({
    mutationFn: () => {
      const entrada = {
        reason: justificativa.trim(),
        ...(motivo ? { decline_reason_id: motivo } : {}),
      };
      return cancelamento
        ? quoteTimeline.cancel(operationId, entrada)
        : quoteTimeline.deny(operationId, entrada);
    },
    onSuccess: () => {
      toast.success(cancelamento ? 'Cotacao cancelada.' : 'Cotacao negada.');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
      void queryClient.invalidateQueries({ queryKey: ['quote-timeline', operationId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      fechar();
    },
  });
  const erro = enviar.error instanceof ApiError ? enviar.error : null;

  function fechar() {
    setMotivo('');
    setJustificativa('');
    enviar.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={cancelamento ? 'Cancelar cotacao' : 'Declinar operacao'}
      description={
        cancelamento
          ? 'O cancelamento encerra a cotacao com a situacao Cancelada e nao tem volta.'
          : 'O declinio encerra a esteira e nao tem volta. O aviso ao corretor ainda nao sai (regra pendente).'
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Voltar
          </Button>
          <Button
            variant="danger"
            loading={enviar.isPending}
            disabled={motivo === '' && justificativa.trim() === ''}
            onClick={() => enviar.mutate()}
          >
            {cancelamento ? 'Cancelar cotacao' : 'Declinar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {enviar.error && !fieldErrorOf(erro, 'reason') ? (
          <Alert tone="danger" title="Nao foi possivel concluir">
            {enviar.error.message}
          </Alert>
        ) : null}
        {opcoes.length > 0 ? (
          <Select
            label="Motivo padronizado"
            placeholder="Sem motivo padronizado"
            options={opcoes}
            value={motivo}
            error={fieldErrorOf(erro, 'decline_reason_id')}
            onChange={(event) => setMotivo(event.target.value)}
          />
        ) : null}
        <Textarea
          label="Justificativa"
          value={justificativa}
          rows={3}
          maxLength={2000}
          error={fieldErrorOf(erro, 'reason')}
          hint="O motivo fica registrado na linha do tempo e na trilha de auditoria."
          onChange={(event) => setJustificativa(event.target.value)}
        />
      </div>
    </Modal>
  );
}
